// Routeur en mémoire, sur le hash. Passer de l'étape 04 à la 05 est un
// changement d'état, zéro requête, instantané (§2.1) — mais l'URL reste
// partageable et le rafraîchissement retombe au bon endroit.
//
//   #/                         accueil
//   #/run/2026-08-11           la run, à l'étape courante
//   #/run/2026-08-11/05        une étape précise
//   #/run/2026-08-11/bilan     le bilan
//   #/journal/2026-08-11       le journal de la journée

import { useEffect, useState } from 'react'

export type Route =
  | { vue: 'accueil' }
  | { vue: 'reglages' }
  | { vue: 'run'; date: string; etape: string | null }
  | { vue: 'bilan'; date: string }
  | { vue: 'journal'; date: string }
  | { vue: 'remise'; date: string }

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export function parse(hash: string): Route {
  const parts = hash.replace(/^#\/?/, '').split('/').filter(Boolean)
  const [tete, date, suite] = parts

  if (tete === 'run' && date && DATE_RE.test(date)) {
    if (suite === 'bilan') return { vue: 'bilan', date }
    return { vue: 'run', date, etape: suite ?? null }
  }
  if (tete === 'journal' && date && DATE_RE.test(date)) return { vue: 'journal', date }
  if (tete === 'remise' && date && DATE_RE.test(date)) return { vue: 'remise', date }
  if (tete === 'reglages') return { vue: 'reglages' }
  return { vue: 'accueil' }
}

export function chemin(r: Route): string {
  switch (r.vue) {
    case 'run':     return r.etape ? `#/run/${r.date}/${r.etape}` : `#/run/${r.date}`
    case 'bilan':   return `#/run/${r.date}/bilan`
    case 'journal': return `#/journal/${r.date}`
    case 'remise':  return `#/remise/${r.date}`
    case 'reglages': return '#/reglages'
    default:        return '#/'
  }
}

export function aller(r: Route, remplacer = false): void {
  const url = chemin(r)
  if (window.location.hash === url) return
  if (remplacer) window.history.replaceState(null, '', url)
  else window.location.hash = url
  window.dispatchEvent(new HashChangeEvent('hashchange'))
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parse(window.location.hash))
  useEffect(() => {
    const onChange = () => setRoute(parse(window.location.hash))
    window.addEventListener('hashchange', onChange)
    window.addEventListener('popstate', onChange)
    return () => {
      window.removeEventListener('hashchange', onChange)
      window.removeEventListener('popstate', onChange)
    }
  }, [])
  return route
}
